import { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { Box } from '@mui/material';

import { TextField, ConfirmDialog, LinearProgress, snackbar } from '@occc-app/design-system';
import { RetailerNetworkLocationGroupFragment } from '@occc/graphql';

import { validateLocationGroup } from './location-group-update-dialog.validation';
import { LocationGroupFormValues } from './location-group-update-dialog.types';

import { useRetailerNetworkLocationGroupUpdateMutation } from '../../../data-access/mutations';

type LocationGroupUpdateDialogProps = {
  locationGroup: RetailerNetworkLocationGroupFragment;
  open: boolean;
  onClose: () => void;
  retailerNetworkUuid: string;
};

export const LocationGroupUpdateDialog = ({
  locationGroup,
  open,
  onClose,
  retailerNetworkUuid,
}: LocationGroupUpdateDialogProps) => {
  const onUpdateCompleted = useCallback(() => {
    snackbar.success('Location Group was updated');
    onClose();
  }, [onClose]);

  const onUpdateError = useCallback(() => {
    snackbar.error('Unable to modify Location Group name. Please try a different name.');
  }, []);

  const [updateLocationGroup, { loading: updateLoading }] =
    useRetailerNetworkLocationGroupUpdateMutation({
      onCompleted: onUpdateCompleted,
      onError: onUpdateError,
      refetchQueries: ['retailerNetworkLocationGroups'],
    });

  const onSubmit = async (values: LocationGroupFormValues) => {
    updateLocationGroup({
      variables: {
        retailerNetworkUuid,
        locationGroup: {
          uuid: locationGroup.uuid,
          name: values.name,
        },
      },
    });
  };

  const initialValues = useMemo(
    () => ({
      name: locationGroup.name,
    }),
    [locationGroup],
  );

  const renderForm = useCallback(() => {
    return <TextField name='name' label='Group Name' />;
  }, []);

  return (
    <Form<LocationGroupFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateLocationGroup}
      render={({ handleSubmit, valid, dirty }) => {
        return (
          <ConfirmDialog
            dialogTitle='Rename Group'
            onClose={onClose}
            open={open}
            maxWidth='xs'
            confirmButtonText='Save'
            confirmButtonProps={{
              onClick: handleSubmit,
              disabled: !valid || !dirty || updateLoading,
            }}
          >
            {updateLoading && <LinearProgress />}
            <Box mt={1}>{renderForm()}</Box>
          </ConfirmDialog>
        );
      }}
    />
  );
};
